import { conns } from '@cfg/axiosConfig.tsx';
import type { HeadConfig } from '../types/head';
import type { PageNode, MovePagePayload, UpdateTitlePayload } from '../types/tree';

export interface CmsPageBase {
  slug: string;
  title: string;
  parent_id?: number | null;
  sort_index?: number;
  html?: string;
  css?: string;
  js?: string;
  blocks?: Record<string, unknown> | null;
  is_published?: boolean;
  image_ids?: number[] | null;
}

export interface PageOut extends CmsPageBase {
  id: number;
  created_at: string;
  updated_at?: string;
}

export type PageCreate = CmsPageBase;
export type PageUpdate = Partial<CmsPageBase>;

export async function list() {
  const { data } = await conns<PageOut[]>('cms:pages').get();
  return data;
}

export interface PreviewTokenResponse {
  token: string;
  expires_at: string;
}

export interface PreviewSnapshotPayload {
  slug: string;
  title: string;
  html: string;
  css: string;
  js: string;
  isPublished: boolean;
  pageId?: number;
  blocks?: Record<string, unknown> | null;
  imageIds?: number[] | null;
}

export interface PreviewSnapshotResponse extends PreviewTokenResponse {
  preview_url: string;
}

interface GetBySlugOptions {
  previewToken?: string | null;
  includeDraft?: boolean;
  etag?: string | null;
  lastModified?: string | null;
  onHttpMeta?: (meta: HttpCacheMeta) => void;
}

export interface HttpCacheMeta {
  etag?: string;
  lastModified?: string;
  status: number;
}

export async function getBySlug(slug: string, options?: GetBySlugOptions) {
  if (options?.includeDraft) {
    const response = await conns<PageOut>('cms:pages', ['draft', slug]).get();
    const meta: HttpCacheMeta = { status: response.status };
    options?.onHttpMeta?.(meta);
    return response.data;
  }

  const params = options?.previewToken ? { preview_token: options.previewToken } : {};
  const headers: Record<string, string> = {};
  if (options?.etag) {
    headers['If-None-Match'] = options.etag;
  }
  if (options?.lastModified) {
    headers['If-Modified-Since'] = options.lastModified;
  }

  const config = Object.keys(headers).length ? { headers } : undefined;
  const response = await conns<PageOut>('cms:pages_reader', ['slug', slug]).get(
    params,
    config,
  );
  const meta: HttpCacheMeta = {
    status: response.status,
    etag: response.headers?.etag,
    lastModified: response.headers?.['last-modified'],
  };
  options?.onHttpMeta?.(meta);
  if (response.status === 304) {
    const error = new Error('Not Modified') as Error & {
      response?: { status?: number };
      httpMeta?: HttpCacheMeta;
    };
    error.response = { status: 304 };
    error.httpMeta = meta;
    throw error;
  }
  return response.data;
}

export async function create(payload: PageCreate) {
  const { data } = await conns<PageOut>('cms:pages').post(payload);
  return data;
}

export async function update(id: number, payload: PageUpdate) {
  const { data } = await conns<PageOut>('cms:pages', [id]).put(payload);
  return data;
}

export async function publish(id: number) {
  const { data } = await conns<PageOut>('cms:pages', [id]).put({ is_published: true });
  return data;
}

export async function getHead(id: number) {
  const { data } = await conns<HeadConfig>('cms:pages', [id, 'head']).get();
  return data;
}

interface GetHeadBySlugOptions {
  previewToken?: string | null;
}

export async function getHeadBySlug(slug: string, options?: GetHeadBySlugOptions) {
  const params = options?.previewToken ? { preview_token: options.previewToken } : {};
  const { data } = await conns<HeadConfig>('cms:pages_reader', ['slug', slug, 'head']).get(params);
  return data;
}

export async function getPreviewToken(slug: string) {
  const { data } = await conns<PreviewTokenResponse>('cms:pages', ['preview-token']).post({ slug });
  return data;
}

export async function createPreviewSnapshot(payload: PreviewSnapshotPayload) {
  const body = {
    slug: payload.slug,
    title: payload.title,
    html: payload.html,
    css: payload.css,
    js: payload.js,
    is_published: payload.isPublished,
    page_id: payload.pageId,
    blocks: payload.blocks ?? null,
    image_ids: payload.imageIds ?? null,
  };
  const { data } = await conns<PreviewSnapshotResponse>('cms:pages', ['preview']).post(body);
  return data;
}

export async function previewUrl(payload: PreviewSnapshotPayload) {
  const { preview_url } = await createPreviewSnapshot(payload);
  return preview_url;
}

export async function getTree(parentId?: number | null) {
  const params = parentId !== undefined ? { parent_id: parentId } : {};
  const { data } = await conns<PageNode[]>('cms:pages', ['tree']).get(params);
  return data;
}

export async function updateTitle(id: number, payload: UpdateTitlePayload) {
  const { data } = await conns<PageNode>('cms:pages', [id, 'title']).patch(payload);
  return data;
}

export async function movePage(id: number, payload: MovePagePayload) {
  await conns('cms:pages', [id, 'move']).post(payload);
  return { ok: true };
}

export async function remove(id: number) {
  await conns('cms:pages', [id]).delete();
  return { ok: true };
}
